import React, { useState } from 'react';
import { Box, Button, Typography, Paper, TextField, IconButton, InputAdornment, Link as MuiLink, } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowForward, ArrowBack } from '@mui/icons-material';
import { providerLogin } from "../services/authService";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { validatePin } from '../services/customerSessionDetails';

// Matches the domain itself and any subdomain (e.g. "*.echocare.in").
const matchesDomain = (host, domain) => host === domain || host.endsWith(`.${domain}`);

function LoginPage() {
  const navigate = useNavigate();
  const [showPatientLogin, setShowPatientLogin] = useState(false);
  const [showProviderLogin, setShowProviderLogin] = useState(false);
  const [pin, setPin] = useState('');
  const [email, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [showPassword, setShowPassword] = useState(false);


  const handleClickShowPassword = () => setShowPassword((prev) => !prev);

  const handlePatientLogin = async (e) => {
    e.preventDefault();

    if (pin.length !== 6) {
      setLoginError("Please enter a 6-digit PIN");
      return;
    }

    try {
      const data = await validatePin(pin);
      console.log("✅ PIN Validated:", data);
      if (!data?.customer) {
        setLoginError("Invalid customer data");
        return;
      }

      const customer = data.customer;

      // Save login info
      localStorage.setItem("customer", JSON.stringify(customer));
      localStorage.setItem("role", "customer");

      if (data.message) {
        localStorage.setItem("loginMessage", data.message);
      }

      // --------------------------------------------------
      // 🔥 Check assessment completion BEFORE navigating
      // --------------------------------------------------
      if (customer.assessment_type) {
        try {
          const assessments = JSON.parse(customer.assessment_type);

          const allCompleted = assessments.every(
            a => a.status?.toLowerCase() === "completed"
          );

          if (allCompleted) {
            navigate("/music-therapy", { replace: true });
            return;
          }
        } catch (err) {
          console.error("Invalid assessment JSON", err);
        }
      }

      // Default route when not completed
      navigate("/my-assessments", {
        state: { pin: customer.pin }
      });
      // handle success, e.g. navigate or store data
    } catch (error) {
      console.error("❌ Validation failed:", error);
      setLoginError(error.message || "Invalid PIN");
    }
  };

  const handleProviderLogin = async (e) => {
    e.preventDefault();
    setLoginError(""); // clear previous errors

    const hostname = window.location.hostname;


    try {
      const data = await providerLogin({ email, password });

      if (!data?.user) {
        setLoginError("Invalid login response");
        return;
      }

      const { account_id } = data.user;

      //  ACCOUNT VALIDATION
      if (
        (account_id === 1 && !matchesDomain(hostname, "musicccare.com")) ||
        (account_id === 2 && !matchesDomain(hostname, "echocare.in"))
      ) {
        setLoginError(
          account_id === 1
            ? "Please log in via musicccare.com"
            : "Please log in via research.echocare.in"
        );
        return; //  stop login
      }

      // try {
      //   const data = await providerLogin({ email, password });
      //   if (data?.user) {
      localStorage.setItem("user", JSON.stringify(data.user));
      localStorage.setItem("name", data.user.name);
      localStorage.setItem("account_id", data.user.account_id);
      localStorage.setItem("role", "user");
      // }

      navigate("/patients");
    } catch (error) {
      setLoginError(error.message || "Login failed. Please try again.");
    }
  };

  const handleBackToPortalSelection = () => {
    setShowPatientLogin(false);
    setShowProviderLogin(false);
    setPin('');
    setUsername('');
    setPassword('');
    setLoginError('');
  };
  const hostname = window.location.hostname;

  const getTitle = () => {
    if (matchesDomain(hostname, "echocare.in")) return "echoCare";
    if (matchesDomain(hostname, "musicccare.com")) return "MUSICC Care";
    return "echoCare";
    // default
  };

  const gradientText = {
    fontSize: 14,
    fontWeight: 500,
    opacity: 0.8,
    background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    display: 'inline-block',
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #f8fffe 0%, #f5f3ff 100%)',
      p: 2,
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Background animated circles */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            width: 400,
            height: 400,
            borderRadius: '50%',
            top: -100,
            right: -100,
            background: 'linear-gradient(135deg, #2ECC71, #1ABC9C)',
            opacity: 0.1,
            animation: 'float 20s infinite ease-in-out',
            animationDelay: '0s',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            width: 300,
            height: 300,
            borderRadius: '50%',
            bottom: -50,
            left: -50,
            background: 'linear-gradient(135deg, #1ABC9C, #9B59B6)',
            opacity: 0.1,
            animation: 'float 20s infinite ease-in-out',
            animationDelay: '-5s',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            width: 200,
            height: 200,
            borderRadius: '50%',
            top: '50%',
            left: '10%',
            background: 'linear-gradient(135deg, #9B59B6, #A855F7)',
            opacity: 0.1,
            animation: 'float 20s infinite ease-in-out',
            animationDelay: '-10s',
          }}
        />
      </Box>

      {/* Float keyframes */}
      <Box
        component="style"
        children={`
          @keyframes float {
            0%,100% { transform: translate(0,0) scale(1); }
            25% { transform: translate(20px,-20px) scale(1.05); }
            50% { transform: translate(-10px,20px) scale(0.95); }
            75% { transform: translate(-20px,-10px) scale(1.02); }
          }
        `}
      />
      {/* Logo */}
      <Box
        sx={{
          position: 'absolute',
          top: { xs: 18, sm: 18, md: 24 },
          left: 24,
          zIndex: 1000,
          animation: 'slideDown 1.3s ease-out forwards',
          '@keyframes slideDown': {
            '0%': { transform: 'translateY(-50px)', opacity: 0 },
            '100%': { transform: 'translateY(0)', opacity: 1 },
          },
        }}
      >
        <Box sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1,
          // background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          borderRadius: '16px',
          // padding: '12px 20px',
          // border: '1px solid rgba(255, 255, 255, 0.3)',
          // boxShadow: '0 8px 32px rgba(46, 204, 113, 0.15)'
        }}>
          <img
            src="/echobuddy-logo.svg"
            alt="echoCare Logo"
            style={{
              height: '44px',
              width: 'auto',
              marginTop: "4px"
            }}
          />
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography
              sx={{
                fontSize: '1.4rem',
                fontWeight: 700,
                letterSpacing: 0.8,
                background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              {getTitle()}
            </Typography>

            <Typography
              sx={{
                fontSize: '0.65rem',
                fontWeight: 500,
                letterSpacing: 0.6,
                background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              Heal. Hope. Harmony.
            </Typography>
          </Box>

        </Box>
      </Box>

      {/* Left Side Content */}
      <Box sx={{
        display: { xs: 'none', md: 'flex' },
        flexDirection: 'column',
        color: 'white',
        maxWidth: '50%',
        p: 4,
        zIndex: 1
      }}>
        <Typography variant="caption"
          sx={{
            fontSize: '1.5rem',
            fontWeight: 500,
            letterSpacing: '0.3px',
            background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            display: 'inline-block',
            lineHeight: 1.4,
            mb: 1
          }}>
          Heal. Hope. Harmony.
        </Typography>
        <Typography variant="caption"
          sx={{
            fontSize: '0.9rem',
            fontWeight: 500,
            letterSpacing: '0.3px',
            background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            display: 'inline-block',
            lineHeight: 1.4,
          }}>
          Comprehensive patient management platform with AI-powered mental wellness support and personalized music therapy.
        </Typography>

        <Box sx={{ mt: 4 }}>
          <Typography sx={{ mb: 1 }}>
            🏥 <span style={gradientText}>Healthcare Provider Solutions</span>
          </Typography>

          <Typography sx={{ mb: 1 }}>
            🎵 <span style={gradientText}>Personalized Music Therapy</span>
          </Typography>

          <Typography sx={{ mb: 3 }}>
            📊 <span style={gradientText}>Clinical Assessment Tools</span>
          </Typography>

          {/* Certification Badges - Below Clinical Assessment Tools */}
          <Box sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap'
          }}>


            <Box sx={{ textAlign: 'center', mr: 3 }}>
              <img
                src="/iso.svg"
                alt="ISO Certified"
                style={{ height: 60, width: 70, filter: 'hue-rotate(90deg) saturate(1.2) brightness(1.1)' }}
              />
              <Typography sx={{ mt: 1, fontSize: 12, fontWeight: 500, opacity: 0.8, color: '#777', }}>
                ISO 27001<br />Certified
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center', mr: 1 }}>
              <img
                src="/wfmh.svg"
                alt="WFMH Member" style={{ height: 50 }} />
              <Typography sx={{ mt: 1, fontSize: 12, fontWeight: 500, opacity: 0.8, color: '#777', }}>
                WFMH<br />Member
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <img
                src="/hipa.svg"
                alt="HIPAA Compliant"
                style={{ height: 50, filter: 'hue-rotate(90deg) saturate(1.2) brightness(1.1)' }}
              />
              <Typography sx={{ mt: 1, fontSize: 12, fontWeight: 500, opacity: 0.8, color: '#777', }}>
                HIPAA<br />Compliant
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Portal Selection */}
      <Paper
        elevation={24}
        sx={{
          width: '100%',
          maxWidth: 520,
          borderRadius: 4,
          px: { xs: 3, md: 4 },
          py: { xs: 4, md: 5 },
          bgcolor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(0,0,0,0.05)',
          boxShadow: '0 24px 40px rgba(0, 0, 0, 0.12)',
          zIndex: 1,
          margin: { xs: '0 16px', md: '0 32px' }
        }}
      >
        {!showPatientLogin && !showProviderLogin ? (
          // Portal Selection View
          <>
            <Box sx={{ textAlign: 'center', mb: { xs: 2, sm: 2, md: 4 } }}>
              <Typography
                sx={{
                  color: '#1F2937',
                  fontSize: { xs: 22, md: 32 },
                  fontWeight: 800,
                  mb: 1,
                  background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                Welcome to  {getTitle()}
              </Typography>
              <Typography sx={{
                color: '#64748B',
                fontSize: { xs: 13, sm: 15, md: 16 },
                lineHeight: 1.6
              }}>
                Choose your portal to access personalized healthcare services
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 2, md: 3 } }}>
              <Button
                variant="contained"
                fullWidth
                onClick={() => setShowProviderLogin(true)}
                sx={{
                  borderRadius: 2,
                  height: 64,
                  background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)',
                  color: '#ffffff',
                  fontSize: 18,
                  fontWeight: 700,
                  textTransform: 'none',
                  boxShadow: '0 8px 20px rgba(34, 197, 94, 0.35)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  px: 3,
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 12px 30px rgba(34, 197, 94, 0.5)',
                  }
                }}
              >
                <Box sx={{ textAlign: 'left' }}>
                  <Typography sx={{ fontSize: { xs: 14, sm: 16, md: 18 }, fontWeight: 700 }}>
                    Provider Portal
                  </Typography>
                  <Typography sx={{ fontSize: { xs: 12, sm: 14, md: 16 }, opacity: 0.9 }}>
                    Healthcare professionals & administrators
                  </Typography>
                </Box>
                <ArrowForward sx={{ fontSize: { xs: 20, sm: 22, md: 24 } }} />
              </Button>

              <Button
                variant="outlined"
                fullWidth
                onClick={() => setShowPatientLogin(true)}
                sx={{
                  borderRadius: 2,
                  height: 64,
                  border: '2px solid #E5E7EB',
                  color: '#374151',
                  fontSize: 18,
                  fontWeight: 700,
                  textTransform: 'none',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  px: 3,
                  '&:hover': {
                    border: '2px solid #22c55e',
                    background: 'rgba(34, 197, 94, 0.05)',
                    transform: 'translateY(-2px)',
                  }
                }}
              >
                <Box sx={{ textAlign: 'left' }}>
                  <Typography sx={{ fontSize: { xs: 14, sm: 16, md: 18 }, fontWeight: 700 }}>
                    Patient Portal
                  </Typography>
                  <Typography sx={{ fontSize: { xs: 12, sm: 14, md: 16 }, color: '#64748B' }}>
                    Access your therapy sessions & progress
                  </Typography>
                </Box>
                <ArrowForward sx={{ fontSize: { xs: 20, sm: 22, md: 24 } }} />
              </Button>
            </Box>
          </>
        ) : showPatientLogin ? (
          // Patient PIN Login View
          <>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography
                sx={{
                  color: '#1F2937',
                  fontSize: { xs: 24, md: 28 },
                  fontWeight: 800,
                  mb: 1
                }}
              >
                Patient Portal Login
              </Typography>
              <Typography sx={{
                color: '#64748B',
                fontSize: 16,
                lineHeight: 1.6
              }}>
                Enter your 6-digit PIN to access your portal
              </Typography>
            </Box>

            <form onSubmit={handlePatientLogin}>
              {/* <TextField
                fullWidth
                label="Enter Your 6-Digit PIN"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                placeholder="000000"
                inputProps={{
                  maxLength: 6,
                  style: {
                    textAlign: 'center',
                    fontSize: '1.5em',
                    letterSpacing: '5px'
                  }
                }}
                variant="outlined"
                sx={{ mb: 3 }}
                error={!!loginError}
                helperText={loginError}
              /> */}

              {/* OTP Input Boxes */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  gap: 1.5,
                  mb: 3,
                }}
              >
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <TextField
                    key={i}
                    value={pin[i] || ""}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/, "");
                      const newPin = pin.split("");

                      if (val) {
                        newPin[i] = val;
                        setPin(newPin.join(""));
                        if (i < 5) {
                          document.getElementById(`otp-${i + 1}`)?.focus();
                        }
                      } else {
                        newPin[i] = "";
                        setPin(newPin.join(""));
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Backspace" && !pin[i] && i > 0) {
                        document.getElementById(`otp-${i - 1}`)?.focus();
                      }
                    }}
                    onPaste={(e) => {
                      e.preventDefault();
                      const pasted = e.clipboardData.getData("text").trim();
                      if (/^\d{6}$/.test(pasted)) {
                        setPin(pasted);
                        // focus last box automatically
                        document.getElementById("otp-5")?.focus();
                      }
                    }}
                    id={`otp-${i}`}
                    inputProps={{
                      maxLength: 1,
                      style: {
                        textAlign: "center",
                        fontSize: "1.5em",
                        width: "50px",
                        height: "50px",
                      },
                    }}
                    error={!!loginError}
                  />
                ))}
              </Box>

              {loginError && (
                <Typography
                  sx={{
                    color: "error.main",
                    textAlign: "center",
                    fontSize: 14,
                    mb: 2,
                  }}
                >
                  {loginError}
                </Typography>
              )}

              <Button
                type="submit"
                variant="contained"
                fullWidth
                sx={{
                  borderRadius: 2,
                  height: 48,
                  background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)',
                  color: '#ffffff',
                  fontSize: 16,
                  fontWeight: 700,
                  textTransform: 'none',
                  boxShadow: '0 8px 20px rgba(34, 197, 94, 0.35)',
                  mb: 2,
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 12px 30px rgba(34, 197, 94, 0.5)',
                  }
                }}
              >
                Access Portal
                <ArrowForward sx={{ ml: 1, fontSize: 18 }} />
              </Button>
            </form>

            <Button
              variant="text"
              fullWidth
              onClick={handleBackToPortalSelection}
              sx={{
                color: '#64748B',
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': {
                  background: 'rgba(100, 116, 139, 0.1)',
                }
              }}
            >
              <ArrowBack sx={{ mr: 1, fontSize: 18 }} />
              Back to Portal Selection
            </Button>

            <Typography sx={{
              color: '#94A3B8',
              fontSize: 12,
              textAlign: 'center',
              mt: 3,
              lineHeight: 1.5
            }}>
              Your healthcare provider will give you this PIN after your treatment session.
            </Typography>
          </>
        ) : showProviderLogin ? (
          // Provider Login View
          <>
            <Box sx={{ textAlign: 'center', mb: { xs: 2, sm: 2, md: 4 } }}>
              <Typography
                sx={{
                  color: '#1F2937',
                  fontSize: { xs: 22, md: 32 },
                  fontWeight: 800,
                  mb: 1
                }}
              >
                Provider Portal Login
              </Typography>
              <Typography sx={{
                color: '#64748B',
                fontSize: { xs: 13, sm: 15, md: 16 },
                lineHeight: 1.6
              }}>
                Enter your credentials to access the provider dashboard
              </Typography>
            </Box>

            <form onSubmit={handleProviderLogin}>
              <TextField
                fullWidth
                label="Username"
                value={email}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter username"
                variant="outlined"
                sx={{ mb: 2 }}
                required
              />

              <TextField
                fullWidth
                label="Password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                variant="outlined"
                sx={{
                  mb: { xs: 1.5, sm: 2, md: 3 },
                  backgroundColor: "#e0f7ff", // your sky blue background
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#e0f7ff",
                  },
                }}
                error={!!loginError}
                helperText={loginError}
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" sx={{ backgroundColor: "#e0f7ff", borderRadius: "4px" }}>
                      <IconButton
                        onClick={handleClickShowPassword}
                        edge="end"
                        disableRipple
                        disableFocusRipple
                        sx={{
                          outline: "none",
                          "&:focus": {
                            outline: "none",
                            boxShadow: "none",
                          },
                          backgroundColor: "#e0f7ff",
                        }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end' }}>
                <MuiLink
                  component={Link}
                  to="/forgotpassword"
                  sx={{ color: '#6B7280', fontSize: 13, textDecoration: 'none', '&:hover': { textDecoration: 'underline' }, mb: { xs: 1.5, sm: 2, md: 3 }, }}
                >
                  Forgot Password?
                </MuiLink>
              </Box>

              <Button
                type="submit"
                variant="contained"
                fullWidth
                sx={{
                  borderRadius: 2,
                  height: 48,
                  background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)',
                  color: '#ffffff',
                  fontSize: { xs: 14, sm: 16, md: 16 },
                  fontWeight: 700,
                  textTransform: 'none',
                  boxShadow: '0 8px 20px rgba(34, 197, 94, 0.35)',
                  mb: 2,
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 12px 30px rgba(34, 197, 94, 0.5)',
                  }
                }}
              >
                Login to Provider Portal
                <ArrowForward sx={{ ml: 1, fontSize: 18 }} />
              </Button>
            </form>

            <Button
              variant="text"
              fullWidth
              onClick={handleBackToPortalSelection}
              sx={{
                color: '#64748B',
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': {
                  background: 'rgba(100, 116, 139, 0.1)',
                }
              }}
            >
              <ArrowBack sx={{ mr: 1, fontSize: 18 }} />
              Back to Portal Selection
            </Button>
          </>
        ) : null}


        {(!showPatientLogin && !showProviderLogin) && (
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography sx={{
              color: '#94A3B8',
              fontSize: 12,
              lineHeight: 1.5
            }}>
              🛡️ Secured healthcare platform with HIPAA compliance<br />
              <strong style={{ color: '#64748B' }}>DigiNxtHlt Solutions Pvt Ltd.</strong>
            </Typography>
          </Box>
        )}
      </Paper>
    </Box >
  );
}

export default LoginPage;
